import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import { useToast } from '../contexts/ToastContext';
import {
  adminListPrivacyRequests,
  adminUpdatePrivacyRequestStatus,
  type AdminPrivacyRequest,
} from '../services/api/adminPrivacyRequests';
import { AdminQueueState } from './ui/AdminQueueState';

const STATUS_FILTERS = ['', 'open', 'in_progress', 'closed'];

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

export const AdminPrivacyRequests: React.FC = () => {
  const { showToast } = useToast();
  const [statusFilter, setStatusFilter] = useState('open');
  const [rows, setRows] = useState<AdminPrivacyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await adminListPrivacyRequests(statusFilter || undefined));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load';
      setLoadError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = async (row: AdminPrivacyRequest, status: 'open' | 'in_progress' | 'closed') => {
    setBusyId(row.id);
    try {
      await adminUpdatePrivacyRequestStatus(row.id, status);
      showToast(`Request #${row.id} marked ${status.replace('_', ' ')}.`, 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Update failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Privacy requests"
        description="DSAR, copyright, and marketing opt-out intake. Verify identity before exporting or deleting account data."
      />

      <div className="flex flex-wrap gap-2" role="toolbar" aria-label="Status filter">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`min-h-[40px] px-3 rounded-md text-sm ${
              statusFilter === s ? 'bg-white/10 text-white' : 'text-zinc-400 hover:bg-white/[0.04]'
            }`}
            aria-pressed={statusFilter === s}
          >
            {s === '' ? 'All' : s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3" aria-busy="true" aria-label="Loading privacy requests">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : loadError ? (
        <AdminQueueState
          variant="error"
          title="Could not load privacy requests"
          description={loadError}
          onRetry={() => void load()}
        />
      ) : rows.length === 0 ? (
        <AdminQueueState
          variant={statusFilter ? 'filter-empty' : 'empty'}
          title={statusFilter ? 'No requests match this filter' : 'Privacy request queue is clear'}
          description={
            statusFilter
              ? 'Try a different status or clear the filter.'
              : 'Export, deletion, copyright, and marketing opt-out requests appear here.'
          }
          onClearFilter={statusFilter ? () => setStatusFilter('') : undefined}
        />
      ) : (
        <ul className="space-y-3 list-none p-0 m-0">
          {rows.map((row) => (
            <li key={row.id} className="rounded-lg border border-white/[0.08] bg-[#0e0e0e] px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm text-zinc-400">
                    #{row.id} · {row.request_type} · {row.status}
                    {row.user_id ? ` · user ${row.user_id}` : ' · anonymous'}
                    {row.has_contact_email ? ' · contact on file' : ''}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">Created {formatDate(row.created_at)}</p>
                  {typeof row.details?.message === 'string' && row.details.message ? (
                    <p className="mt-2 text-sm text-zinc-300 whitespace-pre-wrap">{row.details.message}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {row.status !== 'in_progress' ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={busyId === row.id}
                      onClick={() => void setStatus(row, 'in_progress')}
                    >
                      In progress
                    </Button>
                  ) : null}
                  {row.status !== 'closed' ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === row.id}
                      onClick={() => void setStatus(row, 'closed')}
                    >
                      Close
                    </Button>
                  ) : null}
                  {row.status === 'closed' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busyId === row.id}
                      onClick={() => void setStatus(row, 'open')}
                    >
                      Reopen
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminPrivacyRequests;
